import {
  Button,
  Layout,
  Skeleton,
  Table,
  Typography,
  Image,
  Steps,
  Tooltip,
  Input,
  theme,
  Divider,
} from "antd";
const { Content } = Layout;
import React from "react";
import { Org } from "src/models";
const { Title } = Typography;
import { useOrg } from "src/hooks/useOrg";
import {
  GcalConfigType,
  SourceConfig,
} from "pages/api/[orgId]/integrations-client/definitions";
import { useApplicationContext } from "src/hooks/useApplication";
import { CopyIcon } from "src/components/CopyIcon";
import copy from "copy-to-clipboard";
import { CheckOutlined } from "@ant-design/icons";
import { useColorScheme } from "src/hooks/useColorScheme";

enum IntegrationState {
  fetching,
  loaded,
  none,
  configured,
  creating,
  saving,
}
enum CalendarState {
  fetching,
  loaded,
  none,
}
type Calendar = {
  id: string;
  userEmail: string;
  tokenExpiry: string;
};
const columns = [
  {
    title: "User Email",
    dataIndex: "userEmail",
    key: "userEmail",
  },
  {
    title: "Token Expiry",
    dataIndex: "tokenExpiry",
    key: "tokenExpiry",
  },
];

export function GCalendarIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [url, setUrl] = React.useState<string>("");
  const [calendars, setCalendars] = React.useState<Calendar[]>([]);
  const [state, setState] = React.useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [gcalConfig, setGcalConfig] = React.useState<SourceConfig | null>(null);
  const [clientId, setClientId] = React.useState("");
  const [secret, setSecret] = React.useState("");

  const [calendarState, setCalendarState] = React.useState<CalendarState>(
    CalendarState.fetching
  );
  const [pagination, setPagination] = React.useState({
    current: 1, // Current page
    pageSize: 10, // Items per page
    total: 0, // Total number of items
  });
  const fetchedOnce = React.useRef(false);
  const app = useApplicationContext();
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!fetchedOnce.current) {
      return;
    }

    if (gcalConfig == null) {
      setState(IntegrationState.none);
    } else if (gcalConfig?.url) {
      setUrl(gcalConfig.url);
      setState(IntegrationState.configured);
    } else {
      setState(IntegrationState.loaded);
    }
  }, [gcalConfig]);

  React.useEffect(() => {
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getGCalendarConfig(orgId).then((config) => {
          fetchedOnce.current = true;
          setGcalConfig(config);
          setClientId(config.clientId);
          setSecret(config.secret);
          if (config?.url) {
            setUrl(config.url);
            setState(IntegrationState.configured);
          } else if (config) {
            setState(IntegrationState.loaded);
          } else {
            setState(IntegrationState.none);
          }
        });
        break;
      }
      case IntegrationState.creating: {
        createGCalendarConfig(orgId, GcalConfigType.oauth).then((payload) => {
          setGcalConfig(payload.data);
        });
        break;
      }
      case IntegrationState.configured: {
        getGCalendars(orgId, pagination.pageSize, pagination.current).then(
          (calendars) => {
            setCalendars(calendars);
            setCalendarState(CalendarState.loaded);
          }
        );
        break;
      }
      case IntegrationState.saving: {
        updateGCalendarConfig(orgId, {
          ...gcalConfig!,
          clientId,
          secret,
        }).then(() => setState(IntegrationState.fetching));
        break;
      }
    }
  }, [orgId, pagination, gcalConfig, state, clientId, secret]);
  const { token } = theme.useToken();
  const mode = useColorScheme();

  const listItems = [
    {
      description: (
        <p>
          Create a&nbsp;
          <a
            href="https://console.cloud.google.com/projectcreate"
            target="_blank"
            rel="noopener noreferrer"
          >
            google project
          </a>
          :
          <ul>
            <li>name it (e.g. &quot;vzbl&quot;)</li>
            <li>choose your google workspace organization</li>
            <li>choose any location (e.g. your default organization folder)</li>
          </ul>
        </p>
      ),
    },
    {
      description: (
        <p>
          <a
            href="https://console.cloud.google.com/apis/dashboard?project=vzbl"
            target="_blank"
            rel="noopener noreferrer"
          >
            Enable APIs
          </a>
          <ul>
            <li>enable &quot;google calendar API&quot;</li>
          </ul>
          <Image
            style={{
              border: `solid 1px ${token.colorTextSecondary}`,
              borderRadius: 3,
              filter: mode === "dark" ? "invert()" : undefined,
            }}
            preview={false}
            alt="Enabled APIs"
            src="/instructions/gcal/enabled_apis.png"
          />
        </p>
      ),
    },
    {
      description: (
        <p>
          Configure Consent screen
          <ul>
            <li>
              access your&nbsp;
              <a
                href="https://console.cloud.google.com/apis/credentials?project=vzbl"
                target="_blank"
                rel="noopener noreferrer"
              >
                project credentials
              </a>
            </li>
            <li>
              click &quot;Configure Consent Screen&quot; and select &quot;OAuth
              Client ID&quot;
            </li>
            <li>
              when prompted for &quot;user type&quot;, select
              &quot;Internal&quot;
            </li>
            <li>click &quot;Create&quot;</li>
            <li>
              fill in the next screen
              <ul>
                <li>
                  App information: any value, e.g. &quot;vzbl-integration&quot;
                </li>
                <li>App logo: any logo, e.g. </li>
                <li>App domain: &quot;https://vzbl.dev&quot;</li>
                <li>
                  Application privacy policy:
                  &quot;https://www.vzbl.dev/privacy-policy&quot;
                </li>
                <li>Application terms of service: leave blank</li>
                <li>Authorized domains: &quot;vzbl.dev&quot;</li>
                <li>
                  Developer contact information: &quot;support@vzbl.dev&quot;
                </li>
              </ul>
            </li>
            <li>
              fill in the scopes screen
              <ul>
                <li>
                  add scopes &quot;calendar.readonly&quot;,
                  &quot;userinfo.profile&quot;, &quot;userinfo.email&quot;
                </li>
                <li>click &quot;Save and Continue&quot;</li>
                <li>
                  on the summary screen, click &quot;Back to dashboard&quot;
                </li>
              </ul>
            </li>
          </ul>
        </p>
      ),
    },

    {
      description: (
        <p>
          Create OAuth credentials
          <ul>
            <li>
              access your&nbsp;
              <a
                href="https://console.cloud.google.com/apis/credentials?project=vzbl"
                target="_blank"
                rel="noopener noreferrer"
              >
                project credentials
              </a>
            </li>
            <li>
              click &quot;create credentials&quot; and select &quot;OAuth Client
              ID&quot;
            </li>
            <li>
              when prompted for &quot;application type&quot;, select &quot;Web
              application&quot;
            </li>
            <li>
              for &quot;name&quot;, pick a name of your choosing, e.g.
              &quot;vzbl-integration&quot;
            </li>
            <li>
              add an Authorized Javascript Origin; enter
              &quot;https://int.vzbl.dev&quot; for the url
            </li>
            <li>
              add an Authorized redirect Url; enter
              &quot;https://int.vzbl.dev/api/v1/oauth/gcalendar/redirect&quot;
              for the url
            </li>
            <li>click &quot;Create&quot;</li>
            <li>
              On the following screen, copy &quot;Client ID&quot; and
              &quot;Client Secret&quot; and paste them in the form fields below
            </li>
          </ul>
        </p>
      ),
    },
  ];

  const login = () => {
    window.open(url, "_blank");
  };
  const copyUrl = (url: string) => {
    copy(url);
    setCopied(true);
  };

  return state == IntegrationState.none ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create Google Calendar Integration
    </Button>
  ) : [IntegrationState.loaded, IntegrationState.configured].includes(state) ? (
    <>
      {state == IntegrationState.configured && (
        <>
          <Content>
            {url && (
              <>
                <Button type="primary" size="large" onClick={login}>
                  Authorize VZBL to access your Google Calendar
                </Button>
                <Button type="text" onClick={() => copyUrl(url)}>
                  copy this url to share with your team &nbsp;
                  {copied ? (
                    <CheckOutlined />
                  ) : (
                    <CopyIcon style={{ display: "inline" }} />
                  )}
                </Button>
              </>
            )}
          </Content>
          <br />
          <br />
          <Title level={3}>Connected Users</Title>
          {calendarState === CalendarState.fetching && <Skeleton active />}
          {calendarState === CalendarState.loaded && (
            <Table
              dataSource={calendars}
              columns={columns}
              pagination={pagination}
              rowKey={(record) => record.id}
              onChange={(pagination, _filters, _sorter) => {
                setPagination({
                  current: pagination.current || 1,
                  pageSize: pagination.pageSize || 3,
                  total: pagination.total || 0,
                });
              }}
            />
          )}
          <br />
          <br />
        </>
      )}
      {app.isOrgAdmin && (
        <div>
          <p>
            <em>
              The google calendar integration lets individuals grant vzbl
              (read-only) access to their google calendar
            </em>
          </p>
          <Divider />
          <h3>Configure the google calendar integration</h3>
          <div style={{ margin: "8px 32px" }}>
            <Steps
              direction="vertical"
              progressDot
              items={listItems}
              current={listItems.length - 1}
            />
          </div>
          <Tooltip placement="topLeft" title="Enter the OAuth Client ID">
            <Title level={5}>Enter the OAuth Client ID</Title>
            <Input.Password
              allowClear
              visibilityToggle={clientId != "placeholder"}
              defaultValue={clientId}
              style={{ width: "80%" }}
              onChange={(event) => setClientId(event.target.value)}
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="Enter the OAuth Client secret">
            <Title level={5}>Enter the OAuth Client secret</Title>
            <Input.Password
              allowClear
              visibilityToggle={secret != "placeholder"}
              defaultValue={secret}
              style={{ width: "80%" }}
              onChange={(event) => setSecret(event.target.value)}
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="Save clientId and secret.">
            <Button
              type="primary"
              style={{ marginLeft: "10px" }}
              onClick={() => {
                setState(IntegrationState.saving);
              }}
              disabled={(clientId || "") == "" || (secret || "") == ""}
            >
              Save
            </Button>
          </Tooltip>
        </div>
      )}
    </>
  ) : (
    <></>
  );
}

const getGCalendarConfig = async (orgId: Org["id"]) => {
  return fetch(`/api/${orgId}/sources/get-source-config?type=gcalendar`, {
    method: "GET",
  })
    .then((resp) => resp.json())
    .then((payload) => payload.data);
};

const createGCalendarConfig = async (
  orgId: Org["id"],
  kind: GcalConfigType
) => {
  return fetch(`/api/${orgId}/sources/create-source-config?type=gcalendar`, {
    method: "POST",
    body: JSON.stringify({ kind, type: "gcalendar" }),
  })
    .then((resp) => resp.json())
    .then((payload) => payload.data);
};

const updateGCalendarConfig = async (
  orgId: Org["id"],
  config: SourceConfig
) => {
  return fetch(`/api/${orgId}/sources/update-source-config?type=gcalendar`, {
    method: "POST",
    body: JSON.stringify({ ...config, type: "gcalendar" }),
  })
    .then((resp) => resp.json())
    .then((payload) => payload.data);
};

const getGCalendars = async (
  orgId: Org["id"],
  pageSize: number,
  page: number
) => {
  return fetch(
    `/api/${orgId}/sources/get-source?type=gcalendar&pageSize=${pageSize}&page=${page}`,
    {
      method: "GET",
    }
  )
    .then((resp) => resp.json())
    .then((payload) => payload.data);
};
